// src/modules/activity/components/RegisterActivityForm/index.js
import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Card, Form, Button, Row, Col } from 'react-bootstrap';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import RoutePreview from '../RoutePreview';
import GpsLoadingOverlay from '../../../../shared/components/GpsLoadingOverlay';
import './styles.css';

const RegisterActivityForm = ({
    activityData,
    attachments,
    handleChange,
    handleSubmit,
    handleFileChange,
    handleRemoveAttachment,
    isLoading,
    isEditing = false,
    autoFilledFields = new Set(),
    isAnalyzing = false,
    aiSuggestionsEnabled = true,
    onToggleAiSuggestions,
    gpsFilledFields = new Set(),
    isParsingFile = false,
    routePolyline = null,
}) => {
    // Helper to check if a field was auto-filled by AI
    const isAutoFilled = (fieldName) => autoFilledFields.has(fieldName);
    const isGpsFilled = (fieldName) => gpsFilledFields.has(fieldName);

    // Create preview URLs for attachments
    const attachmentPreviews = useMemo(() => {
        if (!attachments || !Array.isArray(attachments)) {
            return [];
        }
        return attachments.map(attachment => {
            if (attachment.url) {
                // Existing attachment from server
                return { ...attachment, previewUrl: attachment.url };
            } else if (attachment.file) {
                // New file upload - create object URL
                return { ...attachment, previewUrl: URL.createObjectURL(attachment.file) };
            }
            return attachment;
        });
    }, [attachments]);

    return (
        <Card className="shadow-sm" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <Card.Header>
                <h4 className="mb-0">
                    {isEditing ? 'Edit Activity' : 'Register New Activity'}
                    {isAnalyzing && (
                        <span className="ms-2 text-muted" style={{ fontSize: '0.9rem' }}>
                            <i className="bi bi-arrow-repeat spinner"></i> Analyzing image...
                        </span>
                    )}
                </h4>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={handleSubmit}>
                    {/* Registration method guide */}
                    {!isEditing && (
                        <div className="register-info-box mb-4">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <i className="bi bi-info-circle-fill text-info"></i>
                                <strong style={{ fontSize: '0.9rem' }}>How to register your activity</strong>
                            </div>
                            <div className="d-flex flex-column gap-1" style={{ fontSize: '0.83rem' }}>
                                <div>
                                    <span className="badge bg-secondary me-2">.fit</span>
                                    From Garmin or sports watch — auto-fills <em>all</em> fields and adds a route map
                                </div>
                                <div>
                                    <span className="badge bg-secondary me-2">.gpx</span>
                                    GPS track file — auto-fills fields and adds a route map
                                </div>
                                <div>
                                    <span className="register-info-manual-badge me-2">Manual</span>
                                    Upload a photo for AI suggestions, or fill in the details below
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Attachments Section */}
                    <div className="mb-4">
                        <div className="mb-3 d-flex align-items-center justify-content-between">
                            <h5 className="mb-0 d-flex align-items-center gap-2">
                                <i className="bi bi-images text-primary"></i>
                                Photos & Files
                            </h5>
                            <div className="d-flex align-items-center gap-2">
                                {isAnalyzing && (
                                    <span className="ai-analyzing-badge">
                                        <i className="bi bi-stars spinner"></i>
                                        <span>Analyzing...</span>
                                    </span>
                                )}
                                {onToggleAiSuggestions && (
                                    <button
                                        type="button"
                                        className={`btn btn-sm ai-toggle-btn ${aiSuggestionsEnabled ? 'active' : ''}`}
                                        onClick={() => onToggleAiSuggestions(!aiSuggestionsEnabled)}
                                        title={aiSuggestionsEnabled ? 'AI suggestions enabled' : 'AI suggestions disabled'}
                                        disabled={isAnalyzing}
                                    >
                                        <i className="bi bi-stars"></i>
                                        <span className="ms-1">AI</span>
                                    </button>
                                )}
                            </div>
                        </div>
                        <Form.Group className="attachment-upload-container">
                            <label htmlFor="attachments" className="attachment-upload-zone">
                                <input
                                    id="attachments"
                                    type="file"
                                    name="attachments"
                                    onChange={handleFileChange}
                                    multiple
                                    accept="image/*,.gpx,.fit"
                                    style={{ display: 'none' }}
                                />
                                <i className="bi bi-cloud-upload" style={{ fontSize: '2rem' }}></i>
                                <p className="mb-1">Click to upload photos or GPS files</p>
                                <small>Supports: JPG, PNG, GPX, FIT</small>
                            </label>

                            {/* GPS Loading Overlay */}
                            <GpsLoadingOverlay isVisible={isParsingFile} />

                            {routePolyline && (
                                <>
                                    <RoutePreview routePolyline={routePolyline} activityType={activityData.type} />
                                    <div className="mt-2 d-flex align-items-center gap-2 text-success" style={{ fontSize: '0.85rem' }}>
                                        <i className="bi bi-map-fill"></i>
                                        {isEditing ? (
                                            <>
                                                <span>Route will be recalculated if you upload a new GPS file</span>
                                                <span className="badge bg-warning ms-2" style={{ fontSize: '0.7rem' }}>Route set</span>
                                            </>
                                        ) : (
                                            <span>Route will appear on the world map after submitting</span>
                                        )}
                                    </div>
                                </>
                            )}

                            {attachmentPreviews && attachmentPreviews.length > 0 && (
                                <div className="attachment-preview-grid mt-3">
                                    {attachmentPreviews.map((attachment, index) => {
                                        const isImage = attachment.type?.startsWith('image/') || attachment.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);

                                        return (
                                            <div key={index} className="attachment-preview-item">
                                                {isImage ? (
                                                    <img
                                                        src={attachment.previewUrl}
                                                        alt={`Activity attachment ${index + 1}`}
                                                    />
                                                ) : (
                                                    <div className="gpx-file-preview">
                                                        <i className="bi bi-file-earmark-text"></i>
                                                        <span>{attachment.name}</span>
                                                    </div>
                                                )}
                                                <button
                                                    type="button"
                                                    className="attachment-remove-btn"
                                                    onClick={() => handleRemoveAttachment(attachments[index])}
                                                    aria-label="Remove attachment"
                                                >
                                                    <i className="bi bi-x"></i>
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Form.Group>
                    </div>
                    
                    {/* Activity Type Section */}
                    <div className="mb-4 pb-4 border-bottom">
                        <h5 className="mb-3 d-flex align-items-center gap-2">
                            <i className="bi bi-tag-fill text-primary"></i>
                            Activity Type
                            {isAutoFilled('type') && (
                                <span className="badge bg-success" style={{ fontSize: '0.7rem' }}>
                                    <i className="bi bi-stars"></i> AI Suggested
                                </span>
                            )}
                            {isGpsFilled('type') && (
                                <span className="badge bg-info" style={{ fontSize: '0.7rem' }}>
                                    <i className="bi bi-geo-alt-fill"></i> GPS
                                </span>
                            )}
                        </h5>
                        <Form.Group>
                            <Form.Label>Type</Form.Label>
                            <Form.Select
                                name="type"
                                value={activityData.type}
                                onChange={handleChange}
                                required
                                className={isAutoFilled('type') ? 'ai-filled-field' : isGpsFilled('type') ? 'gps-filled-field' : ''}
                            >
                                <option value="">Select Activity Type</option>
                                <option value="running">🏃 Running</option>
                                <option value="cycling">🚴 Cycling</option>
                                <option value="hiking">🥾 Hiking</option>
                                <option value="football">⚽ Football</option>
                                <option value="swimming">🏊 Swimming</option>
                                <option value="other">📋 Other</option>
                            </Form.Select>
                        </Form.Group>
                    </div>

                    {/* Statistics Section */}
                    <div className="mb-4 pb-4 border-bottom">
                        <h5 className="mb-3 d-flex align-items-center gap-2">
                            <i className="bi bi-graph-up text-primary"></i>
                            Activity Statistics
                        </h5>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-0">
                                    <Form.Label>
                                        Distance (km)
                                        {isAutoFilled('distance') && (
                                            <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>
                                                <i className="bi bi-stars"></i> AI
                                            </span>
                                        )}
                                        {isGpsFilled('distance') && (
                                            <span className="badge bg-info ms-2" style={{ fontSize: '0.65rem' }}>
                                                <i className="bi bi-geo-alt-fill"></i> GPS
                                            </span>
                                        )}
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="distance"
                                        value={activityData.distance}
                                        onChange={handleChange}
                                        required
                                        step="0.01"
                                        placeholder="0.00"
                                        className={isAutoFilled('distance') ? 'ai-filled-field' : isGpsFilled('distance') ? 'gps-filled-field' : ''}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-0">
                                    <Form.Label>
                                        Calories (kcal)
                                        {isAutoFilled('calories') && (
                                            <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>
                                                <i className="bi bi-stars"></i> AI
                                            </span>
                                        )}
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="calories"
                                        value={activityData.calories}
                                        onChange={handleChange}
                                        min="0"
                                        step="any"
                                        placeholder="0"
                                        className={isAutoFilled('calories') ? 'ai-filled-field' : ''}
                                    />

                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-0">
                                    <Form.Label>
                                        Elevation (m)
                                        {isAutoFilled('elevation') && (
                                            <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>
                                                <i className="bi bi-stars"></i> AI
                                            </span>
                                        )}
                                        {isGpsFilled('elevation') && (
                                            <span className="badge bg-info ms-2" style={{ fontSize: '0.65rem' }}>
                                                <i className="bi bi-geo-alt-fill"></i> GPS
                                            </span>
                                        )}
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="elevation"
                                        value={activityData.elevation}
                                        onChange={handleChange}
                                        step="1"
                                        placeholder="0"
                                        className={isAutoFilled('elevation') ? 'ai-filled-field' : isGpsFilled('elevation') ? 'gps-filled-field' : ''}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>

                    {/* Duration and Date Section */}
                    <div className="mb-4 pb-4 border-bottom">
                        <h5 className="mb-3 d-flex align-items-center gap-2">
                            <i className="bi bi-clock-fill text-primary"></i>
                            Time Details
                        </h5>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>
                                        Duration
                                        {isAutoFilled('duration') && (
                                            <span className="badge bg-success ms-2" style={{ fontSize: '0.65rem' }}>
                                                <i className="bi bi-stars"></i> AI
                                            </span>
                                        )}
                                        {isGpsFilled('duration') && (
                                            <span className="badge bg-info ms-2" style={{ fontSize: '0.65rem' }}>
                                                <i className="bi bi-geo-alt-fill"></i> GPS
                                            </span>
                                        )}
                                    </Form.Label>
                                    <Form.Control
                                        type="time"
                                        name="duration"
                                        value={activityData.duration}
                                        onChange={handleChange}
                                        step="1"
                                        required
                                        className={isAutoFilled('duration') ? 'ai-filled-field' : isGpsFilled('duration') ? 'gps-filled-field' : ''}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Date & Time</Form.Label>
                                    <Flatpickr
                                        value={activityData.date}
                                        onChange={date => handleChange({
                                            target: { name: 'date', value: date[0] }
                                        })}
                                        options={{
                                            enableTime: true,
                                            dateFormat: "Y-m-d H:i",
                                            time_24hr: true
                                        }}
                                        className="form-control"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>


                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isLoading}
                        className="w-100"
                    >
                        {isLoading ? (
                            <>
                                <i className="bi bi-arrow-repeat spinner me-2"></i> Saving...
                            </>
                        ) : isEditing ? (
                            <>
                                <i className="bi bi-check-circle me-2"></i> Update Activity
                            </>
                        ) : (
                            <>
                                <i className="bi bi-plus-circle me-2"></i> Submit Activity
                            </>
                        )}
                    </Button>
                </Form>
            </Card.Body>
        </Card>
    );
};

RegisterActivityForm.propTypes = {
    activityData: PropTypes.shape({
        type: PropTypes.string.isRequired,
        distance: PropTypes.number.isRequired,
        calories: PropTypes.number.isRequired,
        elevation: PropTypes.number.isRequired,
        duration: PropTypes.string.isRequired,
        date: PropTypes.instanceOf(Date).isRequired,
        attachments: PropTypes.array.isRequired
    }).isRequired,
    attachments: PropTypes.array,
    handleChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    handleFileChange: PropTypes.func.isRequired,
    handleRemoveAttachment: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isEditing: PropTypes.bool,
    autoFilledFields: PropTypes.instanceOf(Set),
    isAnalyzing: PropTypes.bool,
    aiSuggestionsEnabled: PropTypes.bool,
    onToggleAiSuggestions: PropTypes.func,
    gpsFilledFields: PropTypes.instanceOf(Set),
    isParsingFile: PropTypes.bool,
    routePolyline: PropTypes.string,
};

RegisterActivityForm.defaultProps = {
    isEditing: false,
    autoFilledFields: new Set(),
    isAnalyzing: false,
    aiSuggestionsEnabled: true,
    onToggleAiSuggestions: null,
    gpsFilledFields: new Set(),
    isParsingFile: false,
    routePolyline: null,
};

export default RegisterActivityForm;